import React, { useEffect, useState } from 'react';
import api from '@api';
import { NavLink, useNavigate } from 'react-router-dom';

import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useDomain } from '@useDomain';

function GotNextGame() {
  const [games, setGames] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [loading, setLoading] = useState(true);
  const { slug, domain } = useDomain();
  const [byes, setByes] = useState([]);
  const navigate = useNavigate();

  const leagueOrder = [
    "Fountain Valley",
    "Tustin Columbus",
    "Irvine Valley College"
  ];

  useEffect(() => {
    if (!slug && !domain) return;

    const fetchGames = async () => {
      try {
        const res = await api.get(`/api/gamesv2`, { params: slug ? { slug } : { domain } });
        setGames(res.data.games);
      } catch (error) {
        console.error("Error fetching games:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchLeagues = async () => {
      try {
        const res = await api.get(`/api/leaguesv2`, { params: slug ? { slug } : { domain } });
        setLeagues(res.data.leagues);
      } catch (error) {
        console.error("Error fetching leagues:", error);
      }
    };

    const fetchByes = async () => {
      try {
        const res = await api.get(`/api/bye-dates/public`, { params: slug ? { slug } : { domain } });
        setByes(res.data.byes || []);
      } catch (err) {
        console.error("Error fetching byes:", err);
      }
    };

    fetchGames();
    fetchLeagues();
    fetchByes();
  }, [domain, slug]);

  const activeGames = games.filter(game => game.season_id);
  let gamesForFilters = activeGames;

  if (selectedLeague) {
    gamesForFilters = gamesForFilters.filter(game => game.leagueId === Number(selectedLeague));
  }

  const uniqueLeagues = [...new Set(activeGames.map(game => game.leagueId).filter(Boolean))]
    .map(id => {
      const league = activeGames.find(g => g.leagueId === id)?.league;
      return league ? { id: league.id, name: league.name } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const indexA = leagueOrder.findIndex(loc => a.name.includes(loc));
      const indexB = leagueOrder.findIndex(loc => b.name.includes(loc));

      if (indexA !== indexB) {
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      return a.name.localeCompare(b.name);
    });

  const displayRows = [...gamesForFilters, ...byes.map((bye, index) => ({
    id: `bye-${index}`,
    date: bye.date,
    teams: bye.teams.map(t => ({ id: t.id, name: t.name })),
    type: 'bye'
  }))].sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time || '00:00'}`);
    const dateB = new Date(`${b.date}T${b.time || '00:00'}`);
    return dateA - dateB;
  });

  if (loading) return null;

  return (
    <div className="schedulePublic-container">
      <div className='schedulePublic-league-name-container'>
        <h2 className="schedulePublic-league-title">
          <div className="date-filter-wrapper">
            <select value={selectedLeague} onChange={(e) => setSelectedLeague(e.target.value)}>
              <option value="">All Leagues</option>
              {uniqueLeagues.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        </h2>
      </div>

      {gamesForFilters.length > 0 ? (
        <div className="schedulePublic-game-container">
          <table className="schedulePublic-game-container-table">
            <thead>
              <tr className="schedule-head-font">
                <th>Date</th>
                <th>Matchup</th>
                <th>Time</th>
                <th>Results</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((entry) => {
                if (entry.type === 'bye') {
                  return (
                    <tr key={entry.id} className="bye-row">
                      <td>{formatInTimeZone(new Date(`${entry.date}T12:00:00`), 'America/Los_Angeles', 'MMMM d, yyyy')}</td>
                      <td>{entry.teams.map(t => t.name).join(', ')} (BYE)</td>
                      <td>BYE</td>
                      <td>BYE</td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={entry.id}
                    onClick={() => navigate(slug ? `/${slug}/games/${entry.id}` : `/games/${entry.id}`)}
                    className="clickable-row"
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{formatInTimeZone(new Date(`${entry.date}T12:00:00`), 'America/Los_Angeles', 'MMMM d, yyyy')}</td>
                    <td>{entry.homeTeam?.name} vs {entry.awayTeam?.name}</td>
                    <td>{entry.time ? format(new Date(`${entry.date}T${entry.time}`), 'h:mm a') : 'TBD'}</td>
                    <td>{entry.score_team1} - {entry.score_team2}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No games available</p>
      )}
    </div>
  );
}

export default GotNextGame;
